import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BellRing, Plus, Pencil, Trash2 } from "lucide-react";
import AlertDialog, { EMPTY_ALERT } from "@/components/AlertDialog";

export const criteria = (a) => [
  a.sectors?.length ? a.sectors.join(", ") : "Tous secteurs",
  a.regions?.length ? a.regions.join(", ") : "Toutes régions",
  a.budget_max ? `≤ ${Number(a.budget_max).toLocaleString("fr-FR")} €` : "Sans limite de budget",
].join(" · ");

export default function AlertsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/alerts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (window.location.hash === "#alertes") setTimeout(() => document.getElementById("alertes")?.scrollIntoView({ behavior: "smooth" }), 300); }, []);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = (a, active) => act(() => api.put(`/alerts/${a.alert_id}`, { name: a.name, sectors: a.sectors, regions: a.regions, budget_max: a.budget_max, active }), active ? "Alerte activée" : "Alerte en pause");
  const remove = (a) => window.confirm("Supprimer cette alerte ?") && act(() => api.delete(`/alerts/${a.alert_id}`), "Alerte supprimée");

  return (
    <section id="alertes" data-testid="alerts-panel" className="bg-[#F8F6F0] border border-[#E0C173]/40 p-6 sm:p-8 scroll-mt-28">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex gap-4">
          <div className="w-12 h-12 bg-[#1B2A4A] flex items-center justify-center shrink-0"><BellRing className="w-6 h-6 text-[#E0C173]" strokeWidth={1.5} /></div>
          <div><h2 className="font-display text-2xl text-[#1B2A4A]">Mes alertes</h2>
            <p className="text-sm text-[#475569] mt-1">Recevez un email dès qu'une entreprise correspondant à vos critères est mise en vente.</p></div>
        </div>
        <Button data-testid="alert-add" onClick={() => setEditing({ ...EMPTY_ALERT })} className="rounded-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white"><Plus className="w-4 h-4 mr-1" /> Créer une alerte</Button>
      </div>
      <div className="mt-6 space-y-3">
        {items.length === 0 && <p data-testid="alerts-empty" className="text-sm text-[#94A3B8] bg-white border border-dashed border-gray-300 p-6 text-center">Aucune alerte pour le moment. Créez-en une pour être prévenu en priorité.</p>}
        {items.map((a) => (
          <div key={a.alert_id} data-testid={`alert-row-${a.alert_id}`} className={`bg-white border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${a.active ? "" : "opacity-60"}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#1B2A4A]">{a.name || "Mon alerte"}</div>
              <div className="text-xs text-[#475569] mt-1 break-words">{criteria(a)}</div>
              <div className="text-xs text-[#C9A85A] mt-1" data-testid={`alert-matches-${a.alert_id}`}>{a.matches} annonce(s) en ligne correspondent déjà · {a.sent_count || 0} email(s) reçu(s)</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Switch data-testid={`alert-active-${a.alert_id}`} checked={a.active} onCheckedChange={(v) => toggle(a, v)} />
              <Button size="sm" variant="ghost" onClick={() => setEditing(a)} data-testid={`alert-edit-${a.alert_id}`} className="rounded-none"><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(a)} data-testid={`alert-delete-${a.alert_id}`} className="rounded-none text-red-600"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <AlertDialog alert={editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={load} />
    </section>
  );
}
